import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  Table,
  Button,
  TableContainer,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  RadioGroup,
  Radio,
  Stack,
  Select,
  Input,
  InputGroup,
  InputRightAddon,
  Text,
  Textarea,
  Flex,
  OrderedList,
  ListItem,
} from "@chakra-ui/react";
import assert from "utils/assert";
import RichTextEditor from "./components/RichTextEditor/RichTextEditor";
import MetricInput from "components/MetricInput";
import { LabelText, ValueText } from "components/text/TextTypes";
import PropInput from "components/PropInput";
import { estimateRowsForTextarea, toArray } from "utils/text-utils";
import FileListWithBrowseOrUpload from "../components/FileListWithBrowseOrUpload";

import QAPart from "./QAPart";
import { toDate } from "utils";
import { isOther } from "components/PropInput";
import { getCountryByISO3166Code, getListOfRivers } from "./helper";
import { useRerender } from "rerenderer";
import { getConditionFilter, checkQuestionPartCondition } from "./check-condition";

export const OPTIONS_COUNTRIES_MARKER = "...COUNTRIES";
export const OPTIONS_RIVERS_MARKER = "...RIVERS";

/**
 * For a list of parts: index is provided
 * For a table: r and c are provided (and c is always > 0)
 * @param {Object} props
 * @param {QAPart} props.qPart The question part from questionData.parts. Like qaPart but without answerValue
 * @param {QAPart} props.qaPart This is a piece of questionData.answer -parsed-> QAPart[]
 * @param {number?} props.r
 * @param {number?} props.c
 * @param {number?} props.index
 * @param {function(): void} props.onChange Call this after changing the answerValue in place
 * @param {boolean} props.readOnly
 * @param {string} props.reason why the value is disabled or read-only (e.g. "auto-calculated")
 * @returns
 */
export default function QuestionPartEditor({
  qPart,
  qaPart,
  onChange,
  canUseHtml,
  disabled,
  readOnly,
  reason,
  questionData,
  fileInfoList,
  allQuestionDataByRef,
  tags,
}) {
  // link up to setCurrentAnswer rerender
  const { rerender } = useRerender();

  if (!readOnly && qPart?.autoCalculated) {
    readOnly = true;
    reason = "auto-calculated";
  }

  /** @type {string|number|boolean|null} */
  let answerValue = qaPart?.answerValue;
  if (answerValue === undefined && qaPart) {
    answerValue = qaPart?.answer || qaPart?.answerPart; // HACK if the AI used a different label (June 2025)
    if (answerValue !== undefined) {
      console.log("QuestionPartEditor HACK set answerValue from answerPart or answer", qaPart);
      qaPart.answerValue = answerValue;
    }
  }

  /**
   * aPart {answerValue: string|number|boolean|null, unit?: string}
   * Note: aPart is an object to allow complex controls to set multiple properties (e.g. metric: value & unit)
   */
  const setAnswerValue = (aPart) => {
    console.log("QuestionPartEditor setAnswerValue", aPart);
    assert(typeof aPart === "object", "partAnswer must be an object", aPart);
    qaPart.answerValue = aPart.answerValue;
    if (aPart.unit) qaPart.unit = aPart.unit; // HACK
    onChange();
    rerender();
  };
  const part = qPart;

  if (!part) {
    return (
      <Text color="gray.500" fontSize="body.xs">
        No part defined
      </Text>
    );
  }
  // default to textarea - and offer html
  if (part.type === "textarea" || !part.type) {
    if (!canUseHtml) {
      // use a plain textarea for table cells to avoid overly noisy UI
      const rows = estimateRowsForTextarea(answerValue);
      return (
        <Textarea
          value={answerValue || ""}
          onChange={(e) => setAnswerValue({ answerValue: e.target.value })}
          minWidth="200px"
          rows={rows}
          isReadOnly={readOnly}
        />
      );
    }
    return (
      <RichTextEditor
        content={answerValue || ""}
        onChange={(html) => setAnswerValue({ answerValue: html })}
        isDisabled={disabled}
        readOnly={readOnly}
      />
    );
  }
  if (part.type === "text") {
    return (
      <Input
        type="text"
        value={answerValue || ""}
        onChange={(e) => setAnswerValue({ answerValue: e.target.value })}
        isDisabled={disabled}
        readOnly={readOnly}
        maxLength={part.maxLength}
        title={reason}
      />
    );
  }
  if (part.type === "boolean") {
    return (
      <>
        <RadioGroup value={answerValue} title={reason}>
          <Stack direction="row">
            <Radio
              value={true}
              onChange={() => setAnswerValue({ answerValue: true })}
              isDisabled={disabled}
              readOnly={readOnly}
            >
              Yes
            </Radio>
            <Radio
              value={false}
              onChange={() => setAnswerValue({ answerValue: false })}
              isDisabled={disabled}
              readOnly={readOnly}
            >
              No
            </Radio>
            <Text
              style={
                /* Note: styling is tuned to suit use in tables when things can be narrow */ {
                  padding: "10px 0 0 0",
                  margin: 0,
                  fontSize: "10px",
                  cursor: "pointer",
                }
              }
              onClick={() => setAnswerValue({ answerValue: "" })}
            >
              clear
            </Text>
          </Stack>
        </RadioGroup>
      </>
    );
  }
  if (part.type === "number") {
    return (
      <Input
        type="number"
        value={answerValue || ""}
        onChange={(e) => setAnswerValue({ answerValue: e.target.value })}
        isDisabled={disabled}
        readOnly={readOnly}
        title={reason}
      />
    );
  }
  if (part.type === "year") {
    // TODO help on calendar vs financial year
    return (
      <Input
        type="number"
        value={answerValue || ""}
        onChange={(e) => setAnswerValue({ answerValue: e.target.value })}
        min={2000}
        max={2100}
        placeholder="YYYY"
        isDisabled={disabled}
        readOnly={readOnly}
        title={reason}
      />
    );
  }
  if (part.type === "percentage") {
    let numericValue = answerValue;
    if (typeof answerValue === "string" && answerValue.trim().endsWith("%")) {
      numericValue = answerValue.trim().slice(0, -1);
    }
    return (
      <InputGroup title={reason}>
        <Input
          type="number"
          value={numericValue || ""}
          onChange={(e) => setAnswerValue({ answerValue: e.target.value })}
          isDisabled={disabled}
          readOnly={readOnly}
        />
        <InputRightAddon>%</InputRightAddon>
      </InputGroup>
    );
  }
  if (part.type === "metric") {
    return (
      <MetricInput
        metricType={part.metricType}
        unit={part.unit}
        unitValue={qaPart?.unit}
        numericValue={answerValue}
        onChange={(e) => setAnswerValue({ answerValue: e.target.value?.numericValue, unit: e.target.value?.unitValue })}
        disabled={disabled}
        readOnly={readOnly}
        reason={reason}
      />
    );
  }
  if (part.type === "select") {
    if (!part.options) {
      console.error("select without options", part);
      return (
        <Text color="gray.500" fontSize="body.xs">
          No options defined
        </Text>
      );
    }
    // HACK: allow some flex on csv string vs array for _options_ (not value)
    let options = toArray(part.options);
    // HACK handle special options <COUNTRIES> and <RIVERS>
    if (options.includes(OPTIONS_COUNTRIES_MARKER)) {
      const i = options.indexOf(OPTIONS_COUNTRIES_MARKER);
      const country4code = getCountryByISO3166Code();
      // const countryOptions = Object.entries(country4code).map(([code, name]) => ({label: name, value: code}));
      const countryOptions = Object.values(country4code); // HACK just the names, no iso-codes for now (Note: CDP UI uses names)
      options = options
        .slice(0, i)
        .concat(countryOptions)
        .concat(options.slice(i + 1));
    }
    if (options.includes(OPTIONS_RIVERS_MARKER)) {
      const i = options.indexOf(OPTIONS_RIVERS_MARKER);
      const listOfRivers = getListOfRivers();
      options = options
        .slice(0, i)
        .concat(listOfRivers.map((r) => ({ label: r, value: r })))
        .concat(options.slice(i + 1));
    }
    // process any conditions
    options = options
      .map((option) => {
        const optionCondition = getConditionFilter(option);
        if (!optionCondition) return option;
        try {
          const relevant = checkQuestionPartCondition({
            condition: optionCondition,
            part: part,
            answerParts: [qaPart], // This answerParts might be wrong (missing a bit) for some corner cases
            questionData,
            allQuestionDataByRef,
            tags,
          });
          // console.warn("optionCondition", optionCondition, option, part, relevant);
          if (!relevant) {
            return null;
          }
          return option.replace("[" + optionCondition + "]", "").trim();
        } catch (e) {
          console.error("(swallow) Error processing condition", e, optionCondition, option, part);
          return option;
        }
      })
      .filter((x) => !!x);
    // HACK other?
    const canInputOther = options.some(isOther);
    // Note: PropInput handles multiple true/false -- which is a bit ugly
    return (
      <PropInput
        label=""
        type="select"
        multiple={part.multiple}
        grouped={part.grouped}
        canBeUnset
        options={options}
        prop="answerValue"
        item={qaPart}
        onChange={(e) => setAnswerValue({ answerValue: e.target.value })}
        disabled={disabled}
        readOnly={readOnly}
        reason={reason}
        canInputOther={canInputOther}
      />
    );
  }
  if (part.type === "date") {
    const onDateChange = (e) => {
      //   console.log("onDateChange", e.target.value);
      const dv = toDate(e.target.value);
      const s = dv.toISOString().split("T")[0]; // date-part only
      setAnswerValue({ answerValue: s });
    };
    return (
      <Input
        type="date"
        value={answerValue || ""}
        onChange={onDateChange}
        isDisabled={disabled}
        isReadOnly={readOnly}
        title={reason}
      />
    );
  }
  if (part.type === "file") {
    // files from attachments on the question
    const multiple = part.multiple;
    const fileOptions = fileInfoList?.map((fi) => ({ label: fi.filename || fi.name || fi.id, value: fi.id })) || [];
    return (
      <>
        <PropInput
          label=""
          type="select"
          multiple={multiple}
          canBeUnset={true}
          options={fileOptions}
          prop="answerValue"
          item={qaPart}
          onChange={(e) => setAnswerValue({ answerValue: e.target.value })}
          disabled={disabled}
          readOnly={readOnly}
          reason={reason}
        />
        {!fileInfoList?.length && (
          <Text color="gray.500" fontSize="body.xs">
            Attach a reference file below, then select it here.
          </Text>
        )}
      </>
    );
  } // .type === "file"
  if (part.type === "fixed") {
    return <Text>{answerValue}</Text>;
  }
  // should not happen
  return (
    <Flex direction="column" gap={2}>
      <LabelText color="headingGrey" fontSize="body.xs">
        {JSON.stringify(part)}
      </LabelText>
    </Flex>
  );
}
